// The access-request inbox's two writes — the state half of
// `<AdminAccessRequestsPanel/>`, split out the way `createGrantActions` is: a
// mutation with a form step, a per-row busy key and a failure notice is STATE,
// not rendering, and it unit-tests without a shell.
//
// Four rules:
//
//   - **Both writes RE-READ, never patch.** The invite answers a provision
//     result and the decline answers the decided row, but the LIST derives
//     `status` server-side; the caller's `onWritten` is the re-read both end in.
//   - **The accept link is held HERE, once.** The provision answer is the only
//     place the one-time `acceptUrl` ever appears (`lib/admin-access-requests.ts`),
//     so it lives in `invited()` until the operator closes the dialog — and
//     closing DROPS it, on purpose: a link that lingers in a signal after the
//     dialog is gone is a credential nobody can see and nothing can clear.
//   - **Busy is keyed BY ROW.** Two applications are two independent calls; a
//     single "which row is working" slot would re-enable row A the moment row
//     B started, which is how an operator double-fires a decline.
//   - **A refused write renders the API's own sentence**, and the dialog STAYS
//     OPEN over it: a form that closed itself over a 422 would read as success.
//   - **A decline is ASKED, then confirmed** (`createConfirmGate`): it fired on
//     one click beside Invite, while the MCP twin requires `confirm: true`
//     (#5608 slice 17 row 16).
//   - **Both writes ride an operator credential** (#5724). A door refusal sends
//     nothing, lands in `refusal()` for `<OperatorRefusalAlert/>` and CLOSES the
//     invite dialog, which would otherwise sit over the alert: neither refusal
//     is cured from inside the form.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import {
  type AccessRequestProvisioned,
  type AccessRequestView,
  declineAccessRequest,
  type InviteAccessRequestInput,
  inviteAccessRequest,
} from './admin-access-requests';
import { createConfirmGate } from './confirm-gate';
import { createKeyedMutation } from './keyed-mutation';
import type { OperatorRefusal } from './operator-credential';

export type AccessRequestActions = {
  /** True while THIS application has a write in flight. */
  readonly isBusy: (id: string) => boolean;
  /** Failure copy from the last write, or null. The API's `detail` when it sent one. */
  readonly error: () => string | null;
  readonly clearError: () => void;
  /** Why the operator-credential door refused the last write, or null. */
  readonly refusal: () => OperatorRefusal | null;
  /** The application the invite dialog is open for, or null. */
  readonly inviting: () => AccessRequestView | null;
  readonly askInvite: (row: AccessRequestView) => void;
  /** Close the dialog. Drops a held accept link — see the header. */
  readonly closeInvite: () => void;
  /** The provision result of the open dialog's invite, or null until it lands. */
  readonly invited: () => AccessRequestProvisioned | null;
  /** Send the open dialog's form. A second call mid-flight is dropped. */
  readonly invite: (body: InviteAccessRequestInput) => Promise<void>;
  /** The application the decline confirm is open for, or null. */
  readonly declining: () => AccessRequestView | null;
  /** Ask to decline one. Sends nothing — `confirmDecline` does. */
  readonly askDecline: (row: AccessRequestView) => void;
  readonly cancelDecline: () => void;
  /** Decline the asked application. A second call mid-flight is dropped. */
  readonly confirmDecline: () => Promise<void>;
};

/**
 * Write state for one inbox screen. `token` is passed to every call rather than
 * defaulted here so a test drives it without a session, and `onWritten` is the
 * RE-READ both writes end in.
 */
export function createAccessRequestActions(input: {
  readonly token: string | null;
  readonly onWritten: () => void;
}): AccessRequestActions {
  const [inviting, setInviting] = createSignal<AccessRequestView | null>(null);
  const [invited, setInvited] = createSignal<AccessRequestProvisioned | null>(null);
  // ONE keyed mutation for both acts, so both report through ONE error signal —
  // two would have to be synced through a `createEffect`, which is the thing
  // this seam exists to prevent. Keys are `access_requests.id`; the invite and
  // the decline of one row share a key because they are exclusive by design.
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);
  const act = createKeyedMutation({
    fallback: t('admin.access_decline_failed'),
    onStart: () => setRefusal(null),
  });

  const invite = async (body: InviteAccessRequestInput): Promise<void> => {
    const row = inviting();
    if (row === null) return;
    await act.run(
      row.id,
      async () => {
        const out = await inviteAccessRequest(row.id, body, input.token);
        if (out.kind !== 'ok') {
          setRefusal(out.kind);
          if (inviting() === row) setInviting(null);
          return;
        }
        // Show the result only if the dialog is still open for THIS row — the
        // operator may have closed it mid-flight, and a link surfacing on a
        // dialog opened for somebody else would be shown to the wrong name.
        if (inviting() === row) setInvited(out.value);
        input.onWritten();
      },
      t('admin.access_invite_failed'),
    );
  };

  const decline = createConfirmGate<AccessRequestView>({
    act: (row) =>
      act.run(row.id, async () => {
        const out = await declineAccessRequest(row.id, input.token);
        if (out.kind !== 'ok') return void setRefusal(out.kind);
        input.onWritten();
      }),
  });

  return {
    isBusy: act.isBusy,
    error: act.error,
    clearError: act.clearError,
    refusal,
    inviting,
    askInvite: (row) => {
      act.clearError();
      setInvited(null);
      setInviting(row);
    },
    closeInvite: () => {
      setInviting(null);
      setInvited(null);
    },
    invited,
    invite,
    declining: decline.pending,
    askDecline: (row) => {
      act.clearError();
      decline.ask(row);
    },
    cancelDecline: decline.cancel,
    confirmDecline: decline.confirm,
  };
}
